import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Users, Brain, CheckCircle, Sparkle,
  GraduationCap, ClipboardText, ChartLineUp, Target,
  Clock, UserPlus, Rocket, Lightning, ShieldCheck,
  BookOpen, Certificate, Handshake, Star
} from "@phosphor-icons/react";

const AIOnboardingPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      icon: Brain,
      title: "Adaptive Learning Paths",
      description: "AI analyzes each new hire's background and creates personalized onboarding journeys that accelerate competency."
    },
    {
      icon: ClipboardText,
      title: "Role-Based Curricula",
      description: "Customized training tracks for RN Case Managers, Social Workers, Chaplains, Aides, and Administrative staff."
    },
    {
      icon: Target,
      title: "Competency Tracking",
      description: "Real-time dashboards show progress, identify gaps, and trigger interventions before issues arise."
    },
    {
      icon: CheckCircle,
      title: "Automated Compliance",
      description: "Ensures all required training is completed and documented for CMS CoP and CHAP accreditation."
    },
    {
      icon: Clock,
      title: "Time-to-Productivity",
      description: "Reduce new hire ramp-up time by 40% with structured, measurable onboarding milestones."
    },
    {
      icon: Handshake,
      title: "Mentorship Matching",
      description: "AI pairs new hires with optimal mentors based on skills, experience, and learning styles."
    }
  ];

  const phases = [
    {
      number: "01",
      title: "Pre-Boarding Setup",
      duration: "Before Day 1",
      tasks: [
        "Role-specific learning path generated",
        "Mentor assigned automatically",
        "Orientation materials delivered",
        "Equipment and access prepared"
      ]
    },
    {
      number: "02",
      title: "Foundation Week",
      duration: "Days 1-7",
      tasks: [
        "Hospice philosophy and mission",
        "HIPAA and compliance essentials",
        "Documentation fundamentals",
        "EMR system training"
      ]
    },
    {
      number: "03",
      title: "Clinical Immersion",
      duration: "Days 8-30",
      tasks: [
        "Shadowing and field visits",
        "Patient assessment skills",
        "Symptom management protocols",
        "IDG participation"
      ]
    },
    {
      number: "04",
      title: "Independent Practice",
      duration: "Days 31-90",
      tasks: [
        "Supervised caseload management",
        "Competency validations",
        "Performance feedback loops",
        "Full certification"
      ]
    }
  ];

  const roles = [
    { name: "RN Case Manager", duration: "90-day program", icon: UserPlus },
    { name: "Social Worker", duration: "60-day program", icon: Users },
    { name: "Chaplain", duration: "45-day program", icon: BookOpen },
    { name: "Hospice Aide", duration: "30-day program", icon: Handshake },
    { name: "Administrator", duration: "120-day program", icon: Certificate },
    { name: "Compliance Officer", duration: "90-day program", icon: ShieldCheck }
  ];

  const outcomes = [
    { metric: "40%", label: "Faster Time-to-Productivity" },
    { metric: "65%", label: "Reduction in Early Turnover" },
    { metric: "100%", label: "Compliance Documentation" },
    { metric: "92%", label: "New Hire Satisfaction" }
  ];

  return (
    <div data-testid="ai-onboarding-page">
      {/* Hero Section */}
      <section className="section-padding bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="decorative-blob w-72 h-72 bg-accent/20 bottom-0 left-0" />
        
        <div className="max-w-7xl mx-auto container-padding relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="badge badge-primary mb-6">
                <Brain weight="fill" className="w-4 h-4 mr-2" />
                AI-Driven Onboarding
              </span>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-foreground mb-6 leading-tight">
                Transform New Hires Into{" "}
                <span className="text-gradient">Confident Clinicians</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-8">
                Our AI-powered onboarding system creates personalized learning paths, 
                tracks competencies in real-time, and ensures every new team member 
                is survey-ready from day one.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/schedule-demo"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="onboarding-cta-demo"
                >
                  <Rocket weight="fill" className="w-5 h-5" />
                  Schedule a Demo
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center gap-2"
                >
                  Talk to an Expert
                  <ArrowRight weight="bold" className="w-5 h-5" />
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-3xl p-8 shadow-xl border border-border">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                    <GraduationCap weight="fill" className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">Onboarding Dashboard</h3>
                    <p className="text-sm text-muted-foreground">Real-time progress tracking</p>
                  </div>
                </div>
                
                {/* Sample Progress Cards */}
                <div className="space-y-4">
                  {[
                    { name: "Sarah M.", role: "RN Case Manager", progress: 85, status: "On Track" },
                    { name: "Michael T.", role: "Social Worker", progress: 62, status: "In Progress" },
                    { name: "Jennifer L.", role: "Hospice Aide", progress: 100, status: "Completed" }
                  ].map((person, i) => (
                    <div key={i} className="bg-muted/50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-semibold text-foreground text-sm">{person.name}</p>
                          <p className="text-xs text-muted-foreground">{person.role}</p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          person.status === "Completed" 
                            ? "bg-green-100 text-green-700"
                            : person.status === "On Track"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-amber-100 text-amber-700"
                        }`}>
                          {person.status}
                        </span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${
                            person.progress === 100 ? "bg-green-500" : "bg-primary"
                          }`}
                          style={{ width: `${person.progress}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Outcomes Stats */}
      <section className="py-16 bg-primary">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {outcomes.map((outcome, index) => (
              <motion.div
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <p className="font-display text-4xl md:text-5xl text-white mb-2">{outcome.metric}</p>
                <p className="text-white/70">{outcome.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Platform Features</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Everything You Need for Exceptional Onboarding
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Our comprehensive platform handles every aspect of new hire onboarding, 
              from pre-boarding to full certification.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-8 border border-border hover:border-primary/30 hover:shadow-lg transition-all"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                  <feature.icon weight="duotone" className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-bold text-xl text-foreground mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Onboarding Phases */}
      <section className="section-padding bg-muted/50">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-primary mb-4">The Journey</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground mb-4">
              Structured Path to Excellence
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our proven four-phase approach ensures consistent, measurable outcomes for every new hire.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {phases.map((phase, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-2xl p-6 border border-border relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <span className="text-6xl font-display text-primary/10 absolute top-4 right-4">
                  {phase.number}
                </span>
                <div className="relative">
                  <span className="text-xs font-semibold text-accent bg-accent/10 px-2 py-1 rounded-full">
                    {phase.duration}
                  </span>
                  <h3 className="font-bold text-lg text-foreground mt-4 mb-4">{phase.title}</h3>
                  <ul className="space-y-2">
                    {phase.tasks.map((task, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle weight="fill" className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Role-Based Programs */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="badge badge-accent mb-4">Role-Based Programs</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground mb-4">
              Tailored for Every Position
            </h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {roles.map((role, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl p-6 border border-border text-center hover:border-primary/30 hover:shadow-md transition-all"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <role.icon weight="duotone" className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-sm mb-1">{role.name}</h3>
                <p className="text-xs text-muted-foreground">{role.duration}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section section-padding">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full" />
        </div>
        
        <div className="relative max-w-4xl mx-auto container-padding text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkle weight="fill" className="w-12 h-12 text-white/80 mx-auto mb-6" />
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-white mb-6">
              Ready to Transform Your Onboarding?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-8">
              See how our AI-driven onboarding system can reduce turnover, 
              accelerate productivity, and ensure compliance from day one.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/schedule-demo"
                className="bg-white text-primary font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105 inline-flex items-center justify-center gap-2"
              >
                <Lightning weight="fill" className="w-5 h-5" />
                Schedule a Demo
              </Link>
              <Link
                to="/resources"
                className="border-2 border-white text-white font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:bg-white/10 inline-flex items-center justify-center gap-2"
              >
                Download Onboarding Guide
                <ArrowRight weight="bold" className="w-5 h-5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AIOnboardingPage;
